import React, { useState, useEffect } from 'react';
import { Paper, styled } from '@mui/material';
import RichTextEditor from '../../../../../common/RichTextEditor';
import logo from '../../../../../../assets/img/logo_with_text.png';
import { buildExecutiveSummaryHTML, ReportTemplateData } from '../utils/notesTemplates.util';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2),
  minHeight: '842px', // A4 height
  width: '595px', // A4 width
  backgroundColor: '#fff',
  overflow: 'hidden',
  position: 'relative',
  boxSizing: 'border-box',
  '& [contenteditable="true"]': {
    outline: 'none !important',
    '&:focus': {
      outline: 'none !important',
      background: 'transparent !important',
    },
  },
  '& .ProseMirror': {
    maxWidth: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    padding: '10px 20px',
    '& p': {
      maxWidth: '100%',
    },
    '& h2': {
      fontSize: '14px !important',
      fontWeight: 'bold',
    },
    '& h3': {
      fontSize: '13px !important',
      fontWeight: 'bold',
    },
    '& img.company-logo, & img[alt="CD Construction Consulting"]': {
      width: '150px !important',
      height: 'auto !important',
      display: 'block !important',
      margin: '0 auto 2rem auto !important',
      maxWidth: '150px !important',
    },
  },
}));

interface EditableExecutiveSummaryProps {
  initialData: {
    project: {
      name: string;
      number: string;
      observer: string;
    };
    payment: {
      requestedAmount: string;
      contractor: string;
      application: string;
    };
    completion: {
      percentage: number;
    };
  };
  reportData: ReportTemplateData;
  onChange: (content: string) => void;
  initialHtml?: string;
  reportType?: 'limited' | 'magic';
}

const EditableExecutiveSummary: React.FC<EditableExecutiveSummaryProps> = ({
  initialData,
  reportData,
  onChange,
  initialHtml,
  reportType,
}) => {
  const [content, setContent] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // Only initialize once - don't regenerate on every site conditions change
    if (hasInitialized) return;

    let baseHtml = initialHtml || '';

    // For magic reports, regenerate if we have siteConditions but no saved HTML
    // This ensures site conditions from photos module are reflected on first visit
    const shouldRegenerateForSiteConditions =
      reportType === 'magic' && !baseHtml && reportData.siteConditions && Object.keys(reportData.siteConditions).length > 0;

    // If no saved HTML OR we have site conditions to populate, generate the base template
    if (!baseHtml || shouldRegenerateForSiteConditions) {
      baseHtml = buildExecutiveSummaryHTML(reportData, reportType).replace(
        '<img alt="CD Construction Consulting" class="company-logo" />',
        `<img src="${logo}" alt="CD Construction Consulting" class="company-logo" />`,
      );
    }

    setContent(baseHtml);
    setHasInitialized(true);
  }, [initialHtml, initialData, reportData, reportType, hasInitialized]);

  const handleChange = (newContent: string) => {
    setContent(newContent);
    onChange(newContent);
  };

  return (
    <StyledPaper elevation={2}>
      <RichTextEditor value={content} onChange={handleChange} height={842} scale={1} />
    </StyledPaper>
  );
};

export default EditableExecutiveSummary;
